import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Card, CardContent, List, ListItem, ListItemText,
    ListItemSecondaryAction, IconButton, Chip, Button, Grid, Dialog, DialogTitle, 
    DialogContent, DialogActions, Divider, Accordion, AccordionSummary, AccordionDetails,
    Alert
} from '@mui/material';
import {
    PlayArrow as PlayIcon, Description as DocumentIcon, Download as DownloadIcon, 
    ArrowBack as BackIcon, VideoLibrary as VideoIcon, Folder as CategoryIcon,
    Class as ClassIcon, ExpandMore as ExpandMoreIcon, Visibility as ViewIcon
} from '@mui/icons-material';
import axios from 'axios';

const getInitialClassId = () => {
    if (typeof window === 'undefined') return null;

    const classId = new URLSearchParams(window.location.search).get('classId');
    return classId && /^\d+$/.test(classId) ? classId : null;
};

const setClassIdInUrl = (classId) => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    url.searchParams.set('classId', classId);
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
};

const clearClassIdFromUrl = () => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    url.searchParams.delete('classId');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
};

function NewTrainingViewer({ apiBase = '/api/new-training', showStandaloneNotice = true }) {
    const [categories, setCategories] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [loading, setLoading] = useState(false);
    const [viewingClass, setViewingClass] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState({});

    useEffect(() => {
        fetchCategories();
        const initialClassId = getInitialClassId();
        if (initialClassId) {
            fetchClassDetails(initialClassId, false);
        }
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiBase}/categories`);
            setCategories(response.data.data || []);
            
            // Auto-expand first category if only one exists
            if (response.data.data?.length === 1) {
                setExpandedCategories({ [response.data.data[0].id]: true });
            }
        } catch (error) {
            console.error('Error fetching training categories:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchClassDetails = async (classId, updateUrl = true) => {
        try {
            const response = await axios.get(`${apiBase}/classes/${classId}`);
            setSelectedClass(response.data.data);
            setViewingClass(true);
            if (updateUrl) {
                setClassIdInUrl(classId);
            }
        } catch (error) {
            console.error('Error fetching class details:', error);
        }
    };

    const toggleCategoryExpansion = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const canViewInline = (mimeType) => {
        return mimeType === 'application/pdf' || mimeType.startsWith('image/');
    };

    const getFileIcon = (mimeType) => {
        if (mimeType === 'application/pdf') return '📄';
        if (mimeType.includes('word')) return '📝';
        if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📊';
        if (mimeType.includes('excel') || mimeType.includes('sheet')) return '📈';
        if (mimeType.startsWith('image/')) return '🖼️';
        return '📁';
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <Typography>Loading training materials...</Typography>
            </Box>
        );
    }

    if (viewingClass && selectedClass) {
        return (
            <Box>
                <Box display="flex" alignItems="center" mb={3}>
                    <Button
                        startIcon={<BackIcon />}
                        onClick={() => {
                            setViewingClass(false);
                            setSelectedClass(null);
                            clearClassIdFromUrl();
                        }}
                        sx={{ mr: 2 }}
                    >
                        Back to Training
                    </Button>
                    <Box>
                        <Typography variant="h4">
                            {selectedClass.title}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            Category: {selectedClass.category_title}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} lg={8}>
                        {/* Video Section */}
                        {selectedClass.vimeo_video_id && (
                            <Paper sx={{ p: 2, mb: 3 }}>
                                <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                    <VideoIcon sx={{ mr: 1 }} />
                                    Training Video
                                </Typography>
                                <Box sx={{ position: 'relative', width: '100%', height: 0, paddingBottom: '56.25%' }}>
                                    <iframe
                                        src={`https://player.vimeo.com/video/${selectedClass.vimeo_video_id}${selectedClass.vimeo_privacy_hash ? `?h=${selectedClass.vimeo_privacy_hash}&` : '?'}title=0&byline=0&portrait=0&badge=0&autopause=0`}
                                        frameBorder="0"
                                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%'
                                        }}
                                        allowFullScreen
                                        title={selectedClass.title}
                                    />
                                </Box>
                            </Paper>
                        )}

                        {/* Content Section */}
                        {selectedClass.content && (
                            <Paper sx={{ p: 3, mb: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Class Materials
                                </Typography>
                                <div
                                    className="training-class-content"
                                    dangerouslySetInnerHTML={{ __html: selectedClass.content }}
                                />
                            </Paper>
                        )}
                    </Grid>

                    <Grid item xs={12} lg={4}>
                        {/* Class Info */}
                        <Paper sx={{ p: 2, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Class Information
                            </Typography>
                            
                            {selectedClass.description && (
                                <Box mb={2}>
                                    <Typography variant="subtitle2" gutterBottom>Description</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedClass.description}
                                    </Typography>
                                </Box>
                            )}
                            
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <Chip 
                                    icon={<ClassIcon />}
                                    label="Training Class"
                                    color="primary"
                                    size="small"
                                />
                                {selectedClass.vimeo_video_id && (
                                    <Chip 
                                        icon={<VideoIcon />}
                                        label="Has Video"
                                        color="success"
                                        size="small"
                                    />
                                )}
                            </Box>
                        </Paper>

                        {/* Class Documents Section */}
                        {selectedClass.documents && selectedClass.documents.length > 0 && (
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                    <DocumentIcon sx={{ mr: 1 }} />
                                    Class Materials ({selectedClass.documents.length})
                                </Typography>
                                <List dense>
                                    {selectedClass.documents.map((doc) => (
                                        <React.Fragment key={doc.id}>
                                            <ListItem sx={{ pl: 0 }}>
                                                <Box display="flex" alignItems="center" mr={2}>
                                                    <Typography sx={{ fontSize: '1.2em', mr: 1 }}>
                                                        {getFileIcon(doc.mime_type)}
                                                    </Typography>
                                                </Box>
                                                <ListItemText
                                                    primary={doc.original_name}
                                                    secondary={
                                                        <Box>
                                                            <Typography variant="caption" display="block">
                                                                {formatFileSize(doc.file_size)} • {doc.mime_type.split('/')[1].toUpperCase()}
                                                            </Typography>
                                                            {canViewInline(doc.mime_type) && (
                                                                <Chip 
                                                                    label="Can view inline" 
                                                                    size="small" 
                                                                    color="info"
                                                                    sx={{ mt: 0.5 }}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton 
                                                        edge="end"
                                                        onClick={() => window.open(`${apiBase}/classes/${selectedClass.id}/documents/${doc.id}/download`)}
                                                        title="Download"
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            <Divider />
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom display="flex" alignItems="center">
                <CategoryIcon sx={{ mr: 2 }} />
                Training Categories
            </Typography>
            
            <Typography variant="body1" color="text.secondary" paragraph>
                Access your training materials organized by categories. Click on any category to view available classes.
            </Typography>

            {showStandaloneNotice && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    This is the standalone Training system. It is separate from the "TELUS Sales Manual & Instruction" system.
                </Alert>
            )}

            {categories.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
                    <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No training materials available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Check back later for new training content
                    </Typography>
                </Paper>
            ) : (
                <Box>
                    {categories.map((category) => (
                        <Accordion
                            key={category.id}
                            expanded={expandedCategories[category.id] || false}
                            onChange={() => toggleCategoryExpansion(category.id)}
                            sx={{ 
                                mb: 2,
                                '&:before': {
                                    display: 'none',
                                },
                            }}
                        >
                            <AccordionSummary 
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    }
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <CategoryIcon />
                                    <Box>
                                        <Typography variant="h6">{category.title}</Typography>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Chip 
                                                label={`${category.class_count} classes`}
                                                size="small" 
                                                sx={{ 
                                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                                    color: 'inherit'
                                                }}
                                            />
                                            {category.documents && category.documents.length > 0 && (
                                                <Chip 
                                                    icon={<DocumentIcon sx={{ color: 'inherit !important' }} />}
                                                    label={`${category.documents.length} resources`}
                                                    size="small"
                                                    sx={{ 
                                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                                        color: 'inherit'
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 3 }}>
                                <Box>
                                    {category.description && (
                                        <Typography variant="body1" paragraph>
                                            {category.description}
                                        </Typography>
                                    )}

                                    {/* Category Documents */}
                                    {category.documents && category.documents.length > 0 && (
                                        <Box mb={3}>
                                            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                                <DocumentIcon sx={{ mr: 1 }} />
                                                Category Resources
                                            </Typography>
                                            <Grid container spacing={1}>
                                                {category.documents.map((doc) => (
                                                    <Grid item xs={12} sm={6} md={4} key={doc.id}>
                                                        <Card variant="outlined" sx={{ p: 1 }}>
                                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <Typography sx={{ fontSize: '1.2em' }}>
                                                                        {getFileIcon(doc.mime_type)}
                                                                    </Typography>
                                                                    <Box>
                                                                        <Typography variant="body2" noWrap>
                                                                            {doc.original_name}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {formatFileSize(doc.file_size)}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                <IconButton 
                                                                    size="small"
                                                                    onClick={() => window.open(`${apiBase}/categories/${category.id}/documents/${doc.id}/download`)}
                                                                >
                                                                    <DownloadIcon />
                                                                </IconButton>
                                                            </Box>
                                                        </Card>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* Classes */}
                                    <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                        <ClassIcon sx={{ mr: 1 }} />
                                        Training Classes
                                    </Typography>
                                    
                                    {category.classes && category.classes.length > 0 ? (
                                        <Grid container spacing={2}>
                                            {category.classes.map((cls) => (
                                                <Grid item xs={12} md={6} lg={4} key={cls.id}>
                                                    <Card 
                                                        sx={{ 
                                                            height: '100%',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': {
                                                                boxShadow: 6,
                                                                transform: 'translateY(-4px)'
                                                            }
                                                        }}
                                                        onClick={() => fetchClassDetails(cls.id)}
                                                    >
                                                        <CardContent>
                                                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                                <Typography variant="h6" component="div" noWrap>
                                                                    {cls.title}
                                                                </Typography>
                                                                <IconButton size="small" color="primary">
                                                                    <ViewIcon />
                                                                </IconButton>
                                                            </Box>
                                                            
                                                            <Typography variant="body2" color="text.secondary" paragraph>
                                                                {cls.description || 'Click to view this training class'}
                                                            </Typography>
                                                            
                                                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                                {cls.vimeo_video_id && (
                                                                    <Chip 
                                                                        icon={<VideoIcon />} 
                                                                        label="Video" 
                                                                        size="small" 
                                                                        color="success"
                                                                    />
                                                                )}
                                                                {cls.document_count > 0 && (
                                                                    <Chip 
                                                                        icon={<DocumentIcon />} 
                                                                        label={`${cls.document_count} materials`} 
                                                                        size="small" 
                                                                        color="secondary"
                                                                    />
                                                                )}
                                                                <Chip 
                                                                    label={`Order: ${cls.sort_order}`} 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                />
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <Box textAlign="center" py={3}>
                                            <ClassIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                            <Typography variant="body2" color="text.secondary">
                                                No classes available in this category yet
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default NewTrainingViewer;
